<template>
  <div class="modal is-active">
    <div class="modal-background" @click="$emit('cancel')" />
    <div class="modal-card">
      <header class="modal-card-head">
        <p class="modal-card-title">Generate RO-Crate</p>
        <button class="delete" aria-label="close" @click="$emit('cancel')" />
      </header>
      <section class="modal-card-body">
        <!-- WorkflowVersion: attachment flow, no path picker -->
        <p v-if="modelName === 'workflowversions'">
          The RO-Crate will be built and attached to this WorkflowVersion. You
          will receive an email when it is ready.
        </p>

        <!-- WorkflowRun + user has a dropbox: default flow, no picker -->
        <p v-else-if="!customPathAllowed">
          The RO-Crate will be generated and written to your dropbox. You will
          receive an email when it is ready.
        </p>

        <!-- WorkflowRun + no dropbox: must pick a custom target -->
        <template v-else>
          <p class="rgd-help">
            Your account has no dropbox configured, so the RO-Crate needs to be
            written to a custom location. Pick one of the export locations your
            administrator has allowed and, optionally, add a subdirectory —
            LabID will create it if it doesn't exist yet.
          </p>

          <div
            v-if="!allowedRoots.length"
            class="notification is-warning is-light rgd-notice"
          >
            No export locations have been configured by an administrator.
          </div>

          <template v-else>
            <div class="field">
              <label class="label" for="rocrate-root">Export location</label>
              <div class="control">
                <div class="select is-fullwidth">
                  <select id="rocrate-root" v-model="selectedRoot">
                    <option v-for="r in allowedRoots" :key="r" :value="r">
                      {{ r }}
                    </option>
                  </select>
                </div>
              </div>
            </div>

            <div class="field">
              <label class="label" for="rocrate-subpath">
                Subdirectory
                <span class="has-text-weight-normal has-text-grey">
                  (optional)
                </span>
              </label>
              <div class="control">
                <input
                  id="rocrate-subpath"
                  v-model="subpath"
                  class="input is-family-monospace"
                  type="text"
                  placeholder="e.g. project-x/exports"
                />
              </div>
              <p class="help">
                Relative to the export location. Cannot start with
                <code>/</code>
                or contain
                <code>..</code>
                .
              </p>
              <p v-if="preview" class="help rgd-preview">
                RO-Crate will be written under
                <code>{{ preview }}</code>
              </p>
            </div>
          </template>
        </template>
      </section>
      <footer class="modal-card-foot is-justify-content-space-between">
        <BaseButton @click="$emit('cancel')">Cancel</BaseButton>
        <BaseButton
          class="is-primary"
          :disabled="confirmDisabled"
          @click="onConfirm"
        >
          Generate
        </BaseButton>
      </footer>
    </div>
  </div>
</template>

<script>
import { computed, ref, watch } from 'vue'

export default {
  name: 'RocrateGenerateDialog',
  props: {
    modelName: { type: String, required: true },
    customPathAllowed: { type: Boolean, default: false },
    allowedRoots: { type: Array, default: () => [] },
  },
  emits: ['cancel', 'confirm'],
  setup(props, { emit }) {
    const selectedRoot = ref(props.allowedRoots[0] || '')
    const subpath = ref('')

    watch(
      () => props.allowedRoots,
      (roots) => {
        if (!selectedRoot.value && roots.length) selectedRoot.value = roots[0]
      },
    )

    const normalisedSubpath = computed(() =>
      subpath.value.trim().replace(/^\/+/, ''),
    )

    const preview = computed(() => {
      if (!selectedRoot.value) return ''
      const sub = normalisedSubpath.value
      const root = selectedRoot.value.replace(/\/+$/, '')
      return sub ? `${root}/${sub}` : root
    })

    // Client-side sanity check — the server is authoritative.
    const subpathIsInvalid = computed(() => {
      const sub = normalisedSubpath.value
      if (!sub) return false
      if (sub.includes('\0')) return true
      return sub
        .split('/')
        .some((seg) => seg === '' || seg === '..' || seg === '.')
    })

    const confirmDisabled = computed(() => {
      if (props.modelName !== 'workflowruns') return false
      if (!props.customPathAllowed) return false
      if (!props.allowedRoots.length) return true
      if (!selectedRoot.value) return true
      return subpathIsInvalid.value
    })

    const onConfirm = () => {
      if (!props.customPathAllowed) {
        emit('confirm', { root: null, subpath: null })
        return
      }
      emit('confirm', {
        root: selectedRoot.value || null,
        subpath: normalisedSubpath.value || null,
      })
    }

    return {
      selectedRoot,
      subpath,
      preview,
      confirmDisabled,
      onConfirm,
    }
  },
}
</script>

<style lang="scss" scoped>
.rgd-help {
  font-size: 0.9rem;
  color: var(--text-light);
  margin-bottom: 0.75rem;
}

.rgd-notice {
  display: block;
  font-size: 0.875rem;
}

.rgd-preview code {
  word-break: break-all;
}
</style>
